import React from "react"
import styled, { css } from "styled-components"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  h3Styles,
  h4Styles,
  h5Styles,
  smallcapsBold13Styles,
  body28Styles,
  body18Styles,
  paragraphStyles,
  paragraph18Styles,
  subtextStyles,
} from "@constants/typography"
import LinkButton from "@components/buttons/LinkButton"
import * as R from "ramda"

const minimalPaddingStyles = `
  @media ${breakpoints.laptop} {
    padding-left: calc(1 / 12 * 100vw);
    padding-right: calc(1 / 12 * 100vw);

    p {
      ${body18Styles}
    }

    p.finePrint {
      ${subtextStyles}
    }
  }

  @media ${breakpoints.desktop} {
    padding-left: calc(1 / 12 * 100vw);
    padding-right: calc(1 / 12 * 100vw);

    p {
      ${body18Styles}
    }

    p.finePrint {
      ${subtextStyles}
    }
  }
`

const padLeftStyles = `
  @media ${breakpoints.laptop} {
    padding-left: calc(2 / 12 * 100vw);

    p {
      ${paragraph18Styles}
      color: ${colors.mediumDarkGray};
    }

    p.finePrint {
      ${subtextStyles}
      color: ${colors.mediumDarkGray};
    }
  }
`

const padRightStyles = `
  @media ${breakpoints.laptop} {
    padding-right: calc(5 / 12 * 100vw);
  }
`

const Container = styled.div<{
  h3Color?: string
  h4Color?: string
  h6Color?: string
  backgroundColor?: string
  hasHeader?: boolean
  minimalPadding?: boolean
  padLeft?: boolean
  padRight?: boolean
  topPadding?: number
  topPaddingMobile?: number
  bottomPadding?: number
  bottomPaddingMobile?: number
  topMargin?: number
  topMarginMobile?: number
  bottomMargin?: number
  bottomMarginMobile?: number
}>`
  ${({
    h3Color,
    h4Color,
    h6Color,
    backgroundColor,
    hasHeader,
    minimalPadding,
    padLeft,
    padRight,
    topPadding,
    topPaddingMobile,
    bottomPadding,
    bottomPaddingMobile,
    topMargin,
    topMarginMobile,
    bottomMargin,
    bottomMarginMobile,
  }) => css`
    padding: 0 20px 90px;
    margin-top: ${topMarginMobile ? `${topMarginMobile}px` : "0"};
    margin-bottom: ${bottomMarginMobile ? `${bottomMarginMobile}px` : "0"};
    padding-top: ${topPaddingMobile ? `${topPaddingMobile}px` : "0"};
    padding-bottom: ${bottomPaddingMobile
      ? `${bottomPaddingMobile}px`
      : "90px"};
    background-color: ${`#${backgroundColor}` || colors.white};

    h3 {
      ${h4Styles}
      margin-top: 48px;
      margin-bottom: 38px;
      color: ${h3Color ? `#${h3Color}` : colors.black};
    }

    h4 {
      ${h5Styles}
      margin-top: 48px;
      color: ${h4Color ? `#${h4Color}` : colors.brandOrange};
      font-size: 16px;
      line-height: 24px;
    }

    h6 {
      ${smallcapsBold13Styles}
      color: ${h6Color ? `#${h6Color}` : colors.brandOrange};
    }

    a {
      text-decoration: underline;
      color: ${colors.brandOrange};
    }

    p {
      ${paragraphStyles}
      margin-top: 8px;
      margin-bottom: 24px;
      color: ${hasHeader ? colors.mediumDarkGray : colors.black};
    }

    p.finePrint {
      ${subtextStyles}
      margin-top: 8px;
      margin-bottom: 12px;
      color: ${hasHeader ? colors.mediumDarkGray : colors.black};
    }

    ul {
      margin: 0 0 24px;
      padding-left: 18px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    li {
      color: ${hasHeader ? colors.mediumDarkGray : colors.black};

      p {
        ${paragraphStyles}
        color: ${hasHeader ? colors.mediumDarkGray : colors.black};
      }
    }

    @media ${breakpoints.tablet} {
      padding: 0 calc(1 / 12 * 100vw);
      padding-top: ${topPaddingMobile ? `${topPaddingMobile}px` : "0"};
      padding-bottom: ${bottomPaddingMobile
        ? `${bottomPaddingMobile}px`
        : "90px"};

      h3 {
        ${h3Styles}
        margin-bottom: 56px;
        color: ${h3Color ? `#${h3Color}` : colors.black};
      }

      p {
        ${paragraph18Styles}
        margin-bottom: 24px;
        color: ${hasHeader ? colors.mediumDarkGray : colors.black};
      }

      p.finePrint {
        ${subtextStyles}
        margin-bottom: 12px;
        color: ${hasHeader ? colors.mediumDarkGray : colors.black};
      }

      ul {
        margin-bottom: 30px;
      }

      li {
        p {
          ${paragraph18Styles}
          color: ${hasHeader ? colors.mediumDarkGray : colors.black};
        }
      }
    }

    @media ${breakpoints.laptop} {
      margin-top: ${topMargin ? `${topMargin}px` : "0"};
      margin-bottom: ${bottomMargin ? `${bottomMargin}px` : "0"};
      padding: 0 calc(3 / 12 * 100vw) 0 calc(1 / 12 * 100vw);
      padding-top: ${topPadding ? `${topPadding}px` : "0"};
      padding-bottom: ${bottomPadding ? `${bottomPadding}px` : "90px"};

      h3 {
        ${h4Styles}
        margin-bottom: 56px;
        color: ${h3Color ? `#${h3Color}` : colors.black};
      }

      h4 {
        font-size: 18px;
        line-height: 28px;
      }

      p {
        ${paragraph18Styles}
        margin-bottom: 30px;
        color: ${colors.black};
      }

      p.finePrint {
        ${subtextStyles}
        margin-bottom: 12px;
        color: ${colors.black};
      }

      ul {
        margin-bottom: 30px;
      }

      li {
        p {
          ${paragraph18Styles}
          color: ${colors.black};
        }
      }
    }

    @media ${breakpoints.desktop} {
      margin-top: ${topMargin ? `${topMargin}px` : "0"};
      margin-bottom: ${bottomMargin ? `${bottomMargin}px` : "0"};
      padding: 0 calc(3 / 12 * 100vw) 0 calc(1 / 12 * 100vw);
      padding-top: ${topPadding ? `${topPadding}px` : "0"};
      padding-bottom: ${bottomPadding ? `${bottomPadding}px` : "90px"};

      h3 {
        ${h3Styles}
        margin-bottom: 70px;
        color: ${h3Color ? `#${h3Color}` : colors.black};
      }

      h4 {
        font-size: 28px;
        line-height: 42px;
      }

      p {
        ${body28Styles}
        margin-bottom: 30px;
        color: ${colors.black};
      }

      p.finePrint {
        ${subtextStyles}
        margin-bottom: 12px;
        color: ${colors.black};
      }

      ul {
        margin-bottom: 30px;
      }

      li {
        p {
          ${body28Styles}
          color: ${colors.black};
        }
      }
    }

    ${minimalPadding ? minimalPaddingStyles : ""}
    ${padLeft ? padLeftStyles : ""}
    ${padRight ? padRightStyles : ""}
  `}
`

const LinkWrapper = styled.div`
  display: flex;
`

interface TextBlockProps {
  blockId?: string
  content: React.ReactNode
  h3Color?: string
  h4Color?: string
  h6Color?: string
  backgroundColor?: string
  minimalPadding?: boolean
  padLeft?: boolean
  padRight?: boolean
  topPadding?: number
  topPaddingMobile?: number
  bottomPadding?: number
  bottomPaddingMobile?: number
  topMargin?: number
  topMarginMobile?: number
  bottomMargin?: number
  bottomMarginMobile?: number
  cta?: {
    link: string
    text: string
    invert?: boolean
    external?: boolean
    openNewTab?: boolean
  }
}

const TextBlock: React.FC<TextBlockProps> = ({
  blockId,
  content,
  h3Color,
  h4Color,
  h6Color,
  backgroundColor,
  minimalPadding,
  padLeft,
  padRight,
  topPadding,
  topPaddingMobile,
  bottomPadding,
  bottomPaddingMobile,
  topMargin,
  topMarginMobile,
  bottomMargin,
  bottomMarginMobile,
  cta,
}) => {
  const hasHeader = R.find(({ type }) => type && type === "h3", content as any)
  const paddingConfig =
    padLeft || padRight ? { padLeft, padRight } : { minimalPadding }

  return (
    <Container
      id={blockId || undefined}
      h3Color={h3Color}
      h4Color={h4Color}
      h6Color={h6Color}
      backgroundColor={backgroundColor || ""}
      hasHeader={!!hasHeader}
      topPadding={topPadding}
      topPaddingMobile={topPaddingMobile}
      bottomPadding={bottomPadding}
      bottomPaddingMobile={bottomPaddingMobile}
      topMargin={topMargin}
      topMarginMobile={topMarginMobile}
      bottomMargin={bottomMargin}
      bottomMarginMobile={bottomMarginMobile}
      {...paddingConfig}
    >
      {content}
      {cta && (
        <LinkWrapper>
          <LinkButton
            to={cta.link}
            text={cta.text}
            external={cta.external}
            target={cta.openNewTab ? "_blank" : undefined}
          />
        </LinkWrapper>
      )}
    </Container>
  )
}

export default TextBlock
